"use strict";
// Properties in an object aren't ordered. The Map construct in ES6 addresses this issue, but it
// doesn't allow you to modify the order of the items or insert items anywhere but at the end of the
// list. Mapa implements an API similar to Map, but adds some extra functionality. We use a
// doubly-linked list so that we can easily define an orderable list, while providing the ability to
// reference items by keys.
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
// TODO:
// - Open source Mapa as its own project? Mention at
//   https://stackoverflow.com/a/5773972/2831606
const events_1 = __importDefault(require("events"));
class Mapa extends events_1.default.EventEmitter {
    constructor() {
        super();
        this.clear();
    }
    clear() {
        this._firstKey = null;
        this._lastKey = null;
        this._length = 0;
        this._items = {};
    }
    _getItem(key) {
        if (key === undefined || key === null) {
            return undefined;
        }
        else {
            return this._items[key];
        }
    }
    _throwKeyFalsyError() {
        throw new Error('key cannot be null or undefined');
    }
    _setItem(key, item) {
        if (key === undefined || key === null) {
            this._throwKeyFalsyError();
        }
        else {
            this._items[key] = item;
        }
    }
    _deleteItem(key) {
        if (key === undefined || key === null) {
            this._throwKeyFalsyError();
        }
        else {
            delete this._items[key];
        }
    }
    has(key) {
        return this._getItem(key) !== undefined;
    }
    _nullOrUndefined(value) {
        return value === undefined || value === null;
    }
    _getOrThrow(key) {
        // Note: we use items.get() and check for undefined instead of using items.has() so that this
        // provides a guard for TypeScript
        const item = this._getItem(key);
        if (item === undefined) {
            throw new Error(`value is missing for key ${key}`);
        }
        else {
            return item;
        }
    }
    _insertBefore(key, value, beforeKey) {
        let prevKey = this._lastKey;
        if (!this._nullOrUndefined(beforeKey)) {
            prevKey = this._getOrThrow(beforeKey).prevKey;
        }
        else {
            beforeKey = null;
        }
        // Are we appending?
        if (beforeKey === null) {
            // Is there a last item?
            if (this._lastKey !== null) {
                // Link the last item forward to out new item
                this._getOrThrow(this._lastKey).nextKey = key;
            }
            // Update the lastKey
            this._lastKey = key;
        }
        else {
            // Link the next item backwards to our new item
            this._getOrThrow(beforeKey).prevKey = key;
            // Is there a previous item that needs to be linked forward to our new item?
            if (prevKey !== null) {
                this._getOrThrow(prevKey).nextKey = key;
            }
        }
        // Create item that links backwards to the previous item
        const item = { key, prevKey, nextKey: beforeKey, value };
        // Add the new item
        this._setItem(key, item);
        // Is this the 1st item?
        if (this._firstKey === null || beforeKey === this._firstKey) {
            this._firstKey = key;
        }
        // Increment our length counter
        this._length++;
        return item;
    }
    _insertAfter(key, value, afterKey) {
        let nextKey = this._firstKey;
        if (!this._nullOrUndefined(afterKey)) {
            nextKey = this._getOrThrow(afterKey).nextKey;
        }
        else {
            afterKey = null;
        }
        // Are we prepending?
        if (afterKey === null) {
            // Is there a first item?
            if (this._firstKey !== null) {
                // Link the first item backward to out new item
                this._getOrThrow(this._firstKey).prevKey = key;
            }
            // Update the firstKey
            this._firstKey = key;
        }
        else {
            // Link the previous item forward to our new item
            this._getOrThrow(afterKey).nextKey = key;
            // Is there a next item that needs to be linked backwards to our new item?
            if (nextKey !== null) {
                this._getOrThrow(nextKey).prevKey = key;
            }
        }
        // Create item that links backwards to the previous item
        const item = { key, prevKey: afterKey, nextKey, value };
        // Add the new item
        this._setItem(key, item);
        // Is this the last item?
        if (this._lastKey === null || afterKey === this._lastKey) {
            this._lastKey = key;
        }
        // Increment our length counter
        this._length++;
        return item;
    }
    _insert(key, value, beforeKey, afterKey) {
        if (beforeKey !== undefined && afterKey !== undefined) {
            throw new Error('cannot specify both beforeKey and afterKey');
        }
        if (afterKey !== undefined) {
            return this._insertAfter(key, value, afterKey);
        }
        else {
            return this._insertBefore(key, value, beforeKey);
        }
    }
    _delete(key) {
        const item = this._getOrThrow(key);
        // Are we sandwiched between items?
        if (item.prevKey !== null && item.nextKey !== null) {
            // Point the surrounding items to each other
            const prevItem = this._getOrThrow(item.prevKey);
            const nextItem = this._getOrThrow(item.nextKey);
            prevItem.nextKey = item.nextKey;
            nextItem.prevKey = item.prevKey;
        }
        else {
            // Are we deleting the 1st item?
            if (item.prevKey === null) {
                // Is there a next item?
                if (item.nextKey !== null) {
                    // The nextItem no longer has a previous item
                    const nextItem = this._getOrThrow(item.nextKey);
                    nextItem.prevKey = null;
                }
                // Pick the next item as the first item
                this._firstKey = item.nextKey;
            }
            // Are we deleting the last item?
            if (item.nextKey === null) {
                // Is there a previous item?
                if (item.prevKey !== null) {
                    // The prevItem no longer has a next item
                    const prevItem = this._getOrThrow(item.prevKey);
                    prevItem.nextKey = null;
                }
                // Pick the previous item as the last item
                this._lastKey = item.prevKey;
            }
        }
        // Decrement the length
        this._length--;
        // Delete the item
        this._deleteItem(key);
        return item;
    }
    _update(key, value, beforeKey, afterKey) {
        let item = this._getOrThrow(key);
        // Is the item moving?
        if ((beforeKey !== undefined &&
            item.nextKey !== beforeKey &&
            beforeKey !== key) ||
            (afterKey !== undefined && item.prevKey !== afterKey && afterKey !== key)) {
            this._delete(key);
            item = this._insert(key, value, beforeKey, afterKey);
        }
        else {
            // Update
            item.value = value;
        }
        return item;
    }
    emitChange(name, item) {
        // We want to return the complete item so that we can communicate all changes, including when
        // items are moved. We clone the item so that the subscriber doesn't automatically update
        // private data.
        const clonedItem = Object.assign({}, item);
        this.emit(name, clonedItem);
        this.emit('$change', name, clonedItem);
    }
    set(key, value, beforeKey, afterKey) {
        let item = null;
        if (this._nullOrUndefined(key)) {
            this._throwKeyFalsyError();
        }
        else if (this.has(key)) {
            item = this._update(key, value, beforeKey, afterKey);
            this.emitChange('update', item);
        }
        else {
            item = this._insert(key, value, beforeKey, afterKey);
            this.emitChange('create', item);
        }
    }
    get(key) {
        return this._getOrThrow(key).value;
    }
    *walkForward(key, fullEntry) {
        if (this.has(key)) {
            const item = this._getOrThrow(key);
            if (fullEntry) {
                yield [key, item.value];
            }
            else {
                yield item.value;
            }
            // Is there another item?
            if (item.nextKey !== null) {
                yield* this.walkForward(item.nextKey, fullEntry);
            }
        }
    }
    *walkBackward(key, fullEntry) {
        if (this.has(key)) {
            const item = this._getOrThrow(key);
            if (fullEntry) {
                yield [key, item.value];
            }
            else {
                yield item.value;
            }
            // Is there another item?
            if (item.prevKey !== null) {
                yield* this.walkBackward(item.prevKey, fullEntry);
            }
        }
    }
    *values(startKey, reverse) {
        if (reverse) {
            yield* this.walkBackward(startKey ? startKey : this._lastKey);
        }
        else {
            yield* this.walkForward(startKey ? startKey : this._firstKey);
        }
    }
    *entries(startKey, reverse) {
        if (reverse) {
            yield* this.walkBackward(startKey ? startKey : this._lastKey, true);
        }
        else {
            yield* this.walkForward(startKey ? startKey : this._firstKey, true);
        }
    }
    forEach(onValue) {
        for (const entry of this.entries()) {
            const last = entry[0] === this._lastKey;
            const again = onValue(entry[1], entry[0], last);
            // Do we need to exit prematurely?
            if (again === false) {
                break;
            }
        }
    }
    // Alias for forEach
    each(onValue) {
        this.forEach(onValue);
    }
    map(onValue) {
        const values = [];
        this.forEach((value, key) => {
            values.push(onValue(value, key));
        });
        return values;
    }
    delete(key) {
        const item = this._delete(key);
        this.emitChange('delete', item);
        return item;
    }
    length() {
        return this._length;
    }
    previousKey(key) {
        return this._getOrThrow(key).prevKey;
    }
    previous(key) {
        return this.get(this.previousKey(key));
    }
    first() {
        return this.get(this._firstKey);
    }
    firstKey() {
        return this._firstKey;
    }
    hasFirst() {
        return this.has(this._firstKey);
    }
    isEmpty() {
        return !this.hasFirst();
    }
    nextKey(key) {
        return this._getOrThrow(key).nextKey;
    }
    next(key) {
        return this.get(this.nextKey(key));
    }
    last() {
        return this.get(this._lastKey);
    }
    lastKey() {
        return this._lastKey;
    }
    hasLast() {
        return this.has(this._lastKey);
    }
}
exports.default = Mapa;
